"use client";

import type { ReactNode } from "react";
import { Icon } from "@/lib/icons/Icon";
import styles from "./Form.module.css";

/**
 * FormStatus — UNIWERSALNY standard komunikatów dla React-CMS (admin + visitor).
 *
 * **Jest jedynym akceptowanym sposobem pokazywania komunikatów** typu success/
 * error/warning/info w aplikacji. Bez Toast (auto-dismiss), bez ad-hoc div'ów
 * z customowym kolorem. Spójny look across modules — user uczy się raz, działa
 * wszędzie.
 *
 * **Wzorzec wizualny:** left-accent stripe (3px border-left w kolorze variantu)
 * + tinted background (`--color-{variant}-light`) + ikona statusu + text w
 * kolorze variantu + opcjonalny `dismissible` X close button po prawej.
 *
 * **Variants:**
 *   - `error` (czerwony) — backend reject, network fail, walidacja blokująca
 *   - `success` (zielony) — operacja zakończona OK
 *   - `warning` (pomarańczowy) — ostrzeżenie, blokady non-critical
 *   - `info` (niebieski/brand) — informacja kontekstowa, hints
 *
 * **PERSISTENT by design** — banner zostaje w UI dopóki:
 *   1. Kod parent komponentu wyzeruje state (`setError(null)`) — np. przy
 *      nowej próbie submit'u, lub
 *   2. User kliknie ✕ (gdy `dismissible=true`) — wywołuje `onDismiss` callback
 *
 * **Brak auto-dismiss** — błędy/ostrzeżenia MUSZĄ wymusić user-action (kliknięcie
 * X lub naprawa danych). User nie powinien przegapić ostrzeżenia bo "znikło
 * po 3 sek". Auto-dismissujące toasty (snackbary) NIE są częścią standardu.
 *
 * **A11y:** `error` używa `role="alert"` (assertive — screen reader przerywa
 * i czyta natychmiast). `success` używa `role="status"` (polite — czyta po
 * zakończeniu aktualnej wypowiedzi). `warning`/`info` bez role (statyczne
 * od początku, nie wymagają announce). Close button ma `aria-label="Zamknij komunikat"`.
 *
 * @example
 * ```tsx
 * // Form-level error (np. po failed submit):
 * {error && <FormStatus variant="error">{error}</FormStatus>}
 *
 * // Z X close button (user może zignorować i kontynuować):
 * {success && (
 *   <FormStatus variant="success" dismissible onDismiss={() => setSuccess(null)}>
 *     Zmiany zostały zapisane.
 *   </FormStatus>
 * )}
 *
 * // Admin chrome banner (warning o sandbox mode):
 * <FormStatus variant="warning">Tryb sandbox — zmiany nie są zapisywane.</FormStatus>
 * ```
 */
export function FormStatus({
  variant,
  children,
  className,
  dismissible = false,
  onDismiss,
}: FormStatusProps) {
  const variantClass = variantClasses[variant];
  const role =
    variant === "error" ? "alert" : variant === "success" ? "status" : undefined;
  const iconName = ICON_BY_VARIANT[variant];

  return (
    <div
      role={role}
      className={[styles.status, variantClass, className]
        .filter(Boolean)
        .join(" ")}
    >
      <Icon name={iconName} className={styles.statusIcon} />
      <span className={styles.statusText}>{children}</span>
      {dismissible && (
        <button
          type="button"
          className={styles.statusDismiss}
          aria-label="Zamknij komunikat"
          onClick={onDismiss}
        >
          <Icon name="x" />
        </button>
      )}
    </div>
  );
}

const variantClasses = {
  error: styles.statusError,
  success: styles.statusSuccess,
  warning: styles.statusWarning,
  info: styles.statusInfo,
} as const;

/** Phosphor ikona per variant — spójny visual signal niezależnie od koloru
    (dla user-side WCAG nie polega tylko na kolorze; admin też zyskuje). */
const ICON_BY_VARIANT = {
  error: "x-circle",
  success: "check-circle",
  warning: "warning",
  info: "info",
} as const;

export type FormStatusVariant = keyof typeof variantClasses;

export type FormStatusProps = {
  variant: FormStatusVariant;
  children: ReactNode;
  className?: string;
  /** Pokaż przycisk ✕ żeby user mógł zamknąć banner bez wykonania action.
      Wymaga `onDismiss` callback. Default `false` — banner musi być wyzerowany
      programowo przez parent (np. setError(null) przy następnej próbie). */
  dismissible?: boolean;
  /** Callback po kliknięciu ✕. Typowy use: `() => setError(null)`. */
  onDismiss?: () => void;
};
